import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useAuth } from "@clerk/react";

export interface WorkbenchItem {
  id: number;
  createdAt: string;
  updatedAt: string;
  tender: {
    id: number;
    title: string;
    district: string | null;
    status: string;
    deadlineDate: string | null;
    documentUrls: string[];
    referenceUrl: string | null;
  };
  summary: {
    id: number;
    content: string;
    modelId: string;
    isStale: boolean;
    createdAt: string;
    updatedAt: string;
  } | null;
}

export interface WorkbenchFavourite {
  tenderId: number;
  savedAt: string;
  title: string;
  district: string | null;
  status: string;
  deadlineDate: string | null;
  documentCount: number;
}

export interface CompanyProfile {
  id: number;
  name: string;
  overview: string;
  relevantExperience: string;
  industryReputation: string;
  awards: string;
  capabilities: string[];
  certifications: string[];
  isDefault: boolean;
  createdAt: string;
  updatedAt: string;
}

export interface ProfilePolishResult {
  overview: string;
  relevantExperience: string;
  industryReputation: string;
  awards: string;
}

async function authFetch(url: string, getToken: () => Promise<string | null>, options?: RequestInit) {
  const token = await getToken();
  return fetch(url, {
    ...options,
    headers: {
      "Content-Type": "application/json",
      ...options?.headers,
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    },
  });
}

async function handleApiRes(res: Response) {
  if (!res.ok) {
    const json = await res.json().catch(() => ({}));
    const error = new Error(json.error || `伺服器錯誤 (${res.status})`) as Error & { code?: string };
    error.code = typeof json.code === "string" ? json.code : undefined;
    throw error;
  }
  return res.json();
}

export function useGetWorkbenchItems() {
  const { getToken } = useAuth();
  return useQuery({
    queryKey: ["admin-workbench-items"],
    queryFn: async () => {
      const res = await authFetch("/api/admin/ai-workbench", getToken);
      return handleApiRes(res) as Promise<{ items: WorkbenchItem[] }>;
    },
  });
}

export function useGetWorkbenchFavourites() {
  const { getToken } = useAuth();
  return useQuery({
    queryKey: ["admin-workbench-favourites"],
    queryFn: async () => {
      const res = await authFetch("/api/admin/ai-workbench/favourites", getToken);
      return handleApiRes(res) as Promise<{ favourites: WorkbenchFavourite[], workbenchTenderIds: number[] }>;
    },
  });
}

export function useAddWorkbenchItem() {
  const { getToken } = useAuth();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (tenderId: number) => {
      const res = await authFetch("/api/admin/ai-workbench/items", getToken, {
        method: "POST",
        body: JSON.stringify({ tenderId }),
      });
      return handleApiRes(res);
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["admin-workbench-items"] });
      qc.invalidateQueries({ queryKey: ["admin-workbench-favourites"] });
    },
  });
}

export function useRemoveWorkbenchItem() {
  const { getToken } = useAuth();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: number) => {
      const res = await authFetch(`/api/admin/ai-workbench/items/${id}`, getToken, {
        method: "DELETE",
      });
      return handleApiRes(res);
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["admin-workbench-items"] });
      qc.invalidateQueries({ queryKey: ["admin-workbench-favourites"] });
    },
  });
}

export function useGenerateWorkbenchSummary() {
  const { getToken } = useAuth();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: number) => {
      const res = await authFetch(`/api/admin/ai-workbench/items/${id}/summary`, getToken, {
        method: "POST",
      });
      return handleApiRes(res) as Promise<{ summary: any, reused: boolean }>;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["admin-workbench-items"] });
    },
  });
}

export function useGetCompanyProfiles() {
  const { getToken } = useAuth();
  return useQuery({
    queryKey: ["admin-company-profiles"],
    queryFn: async () => {
      const res = await authFetch("/api/admin/company-profiles", getToken);
      return handleApiRes(res) as Promise<{ profiles: CompanyProfile[] }>;
    },
  });
}

export function useCreateCompanyProfile() {
  const { getToken } = useAuth();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (data: Partial<CompanyProfile>) => {
      const res = await authFetch("/api/admin/company-profiles", getToken, {
        method: "POST",
        body: JSON.stringify(data),
      });
      return handleApiRes(res) as Promise<{ profile: CompanyProfile }>;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["admin-company-profiles"] });
    },
  });
}

export function useUpdateCompanyProfile() {
  const { getToken } = useAuth();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, data }: { id: number, data: Partial<CompanyProfile> }) => {
      const res = await authFetch(`/api/admin/company-profiles/${id}`, getToken, {
        method: "PATCH",
        body: JSON.stringify(data),
      });
      return handleApiRes(res);
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["admin-company-profiles"] });
    },
  });
}

export function useDeleteCompanyProfile() {
  const { getToken } = useAuth();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: number) => {
      const res = await authFetch(`/api/admin/company-profiles/${id}`, getToken, {
        method: "DELETE",
      });
      return handleApiRes(res);
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["admin-company-profiles"] });
    },
  });
}

export function usePolishCompanyProfile() {
  const { getToken } = useAuth();
  return useMutation({
    mutationFn: async ({ id, data }: { id: number, data: Partial<CompanyProfile> }) => {
      const res = await authFetch(`/api/admin/company-profiles/${id}/polish`, getToken, {
        method: "POST",
        body: JSON.stringify(data),
      });
      return handleApiRes(res) as Promise<{ polished: ProfilePolishResult }>;
    },
  });
}
